import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KIND_LABELS } from "@/lib/constants";
import { formatDateTime } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PersonnelNotifications() {
  const { user } = useAuth();
  const isCustomer = user.role === "customer";
  const [items, setItems] = useState(null);
  const [firmId, setFirmId] = useState(user.firm_id || "");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ kind: "ise_giris", name: "", surname: "", duty: "", tc: "", birth_date: "", phone: "", note: "" });

  const load = () => api.get("/personnel-notifications").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!firmId) { toast.error("Firma bulunamadı."); return; }
    setSaving(true);
    try {
      await api.post(`/firms/${firmId}/personnel-notifications`, form);
      toast.success("Bildiriminiz alındı. Danışmanınız işleyecektir.");
      setForm({ kind: "ise_giris", name: "", surname: "", duty: "", tc: "", birth_date: "", phone: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const process = async (id) => {
    try { await api.post(`/personnel-notifications/${id}/process`); toast.success("Bildirim işlendi olarak işaretlendi."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-bold text-slate-900">Personel Bildirimleri</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        {isCustomer && (
          <Card className="border-slate-200 h-fit">
            <CardHeader><CardTitle className="font-display text-lg">Yeni Bildirim</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Bildirim Türü</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="notif-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ise_giris">İşe Giriş</SelectItem>
                      <SelectItem value="isten_ayrilma">İşten Ayrılma</SelectItem>
                      <SelectItem value="gorev_degisikligi">Görev Değişikliği</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label>Ad {form.kind === "ise_giris" && "*"}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="notif-name-input" /></div>
                  <div className="space-y-1.5"><Label>Soyad {form.kind === "ise_giris" && "*"}</Label><Input value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="notif-surname-input" /></div>
                </div>
                <div className="space-y-1.5"><Label>Görev {form.kind === "ise_giris" && "*"}</Label><Input value={form.duty} onChange={(e) => setForm({ ...form, duty: e.target.value })} data-testid="notif-duty-input" /></div>
                <div className="space-y-1.5"><Label>Doğum Tarihi (ops.)</Label><Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="notif-birthdate-input" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label>T.C. (ops.)</Label><Input value={form.tc} onChange={(e) => setForm({ ...form, tc: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Telefon (ops.)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label>Not</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
                <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="personnel-change-submit-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Bildirimi Gönder
                </Button>
                <p className="text-xs text-slate-400">T.C., doğum tarihi ve telefon isteğe bağlıdır. Yeni personelde ad, soyad ve görev zorunludur.</p>
              </form>
            </CardContent>
          </Card>
        )}

        <Card className={`border-slate-200 ${isCustomer ? "lg:col-span-2" : "lg:col-span-3"}`}>
          <CardHeader><CardTitle className="font-display text-lg">{isCustomer ? "Bildirim Geçmişim" : "Gelen Bildirimler"}</CardTitle></CardHeader>
          <CardContent>
            {items === null ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> :
              items.length === 0 ? <p className="text-sm text-slate-500 py-4">Bildirim yok.</p> : (
                <div className="divide-y divide-slate-100">
                  {items.map((n) => (
                    <div key={n.id} className="py-3 flex items-start justify-between gap-3" data-testid={`notif-row-${n.id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{n.kind_label}</span>
                          {!isCustomer && <span className="text-xs text-slate-500">· {n.firm_title}</span>}
                        </div>
                        <div className="text-sm text-slate-600">{[n.name, n.surname].filter(Boolean).join(" ")} {n.duty && `— ${n.duty}`}</div>
                        {n.note && <div className="text-xs text-slate-400 mt-0.5">{n.note}</div>}
                        <div className="text-[11px] text-slate-400 mt-0.5">{formatDateTime(n.created_at)}</div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <StatusBadge tone={n.status === "pending" ? "pending" : "active"}>{n.status === "pending" ? "Beklemede" : "İşlendi"}</StatusBadge>
                        {!isCustomer && n.status === "pending" && (
                          <Button size="sm" variant="outline" onClick={() => process(n.id)} data-testid={`notif-process-${n.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> İşle</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
